import * as React from "react";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";

import {
  IUser,
  useUserApi
} from "src/core/users/api/users.api";
import type { IStore } from "src/core/shared/store";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import { setUser } from "src/core/users/store/users.actions";

interface IState {
  isLoading: boolean;
  user: IUser | null;
}

interface IActions {
  updateUser: (user: IUser) => void;
  reloadUser: (id: number) => void;
}

const useUser = (userId: number): [IState, IActions] => {
  const [ isLoading, setLoading ] = React.useState(true);
  const dispatch = useDispatch();
  const { user } = useMappedState(({ users }: IStore) => ({ user: users.user }));
  const [ getUserResponse, getUser ] = useUserApi("GET_USER");

  const updateUser = React.useCallback((user: IUser) => {
    dispatch(setUser(user));
  }, [ dispatch ]);

  // reload persisted user
  const reloadUser = React.useCallback((id: number) => {
    getUser({ id });
  }, [ getUser ]);

  React.useEffect(() => {
    if (!user || user.id !== userId) {
      dispatch(setUser(null));
      getUser({ id: userId });
    } else {
      setLoading(false);
    }
  }, [ getUser ]);

  React.useEffect(() => {
    if (getUserResponse.data) {
      dispatch(setUser(getUserResponse.data));
      setLoading(false);
    }

    if (getUserResponse.error) {
      dispatch(setGlobalError(getUserResponse.error));
    }
  }, [ getUserResponse.data ]);

  return [
    {
      isLoading,
      user
    },
    {
      updateUser,
      reloadUser
    }
  ];
};

export default useUser;
